// src/components/YaraCard.tsx
import React, { useState } from 'react';
import { Shield, AlertCircle, XCircle } from 'lucide-react';
import './Card.css';

interface YaraCardProps {
  yaraData: {
    rule_name?: string;
    rule?: string;
    valid?: boolean;
    meta?: {
      byte_signatures_extracted: number;
      required_matches: number;
    };
    skipped?: boolean;
    reason?: string;
    warning?: string;
    error?: string;
  };
}

const YaraCard: React.FC<YaraCardProps> = ({ yaraData }) => {
  const [copied, setCopied] = useState(false);

  const copyRule = () => {
    if (yaraData.rule) {
      navigator.clipboard.writeText(yaraData.rule);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // --- Skipped: file was clean per VT ---
  if (yaraData.skipped) {
    return (
      <div className="yara-card">
        <div className="yara-header">
          <h3 className="card-title">🎯 Generated YARA Rule</h3>
        </div>
        <div className="yara-state-message yara-clean">
          <Shield size={32} className="yara-state-icon" />
          <p className="yara-state-text">YARA rule generation skipped</p>
          <span className="yara-state-note">{yaraData.reason}</span>
        </div>
      </div>
    );
  }

  // --- Error: generation failed ---
  if (yaraData.error) {
    return (
      <div className="yara-card">
        <div className="yara-header">
          <h3 className="card-title">🎯 Generated YARA Rule</h3>
        </div>
        <div className="yara-state-message yara-error-state">
          <XCircle size={32} className="yara-state-icon" />
          <p className="yara-state-text">Rule generation failed</p>
          <span className="yara-state-note">{yaraData.error}</span>
        </div>
      </div>
    );
  }

  // --- Normal or warning (suspicious / unverified) ---
  return (
    <div className="yara-card">
      <div className="yara-header">
        <h3 className="card-title">🎯 Generated YARA Rule</h3>
        <div className="yara-actions">
          <span className={`valid-badge ${yaraData.valid ? 'valid' : 'invalid'}`}>
            {yaraData.valid ? '✓ Valid Rule' : '✗ Invalid'}
          </span>
          <button className="copy-rule-button" onClick={copyRule}>
            {copied ? '✓ Copied!' : '📋 Copy rule'}
          </button>
        </div>
      </div>

      {/* Warning banner for low-confidence or unverified rules */}
      {yaraData.warning && (
        <div className="yara-warning-banner">
          <AlertCircle size={14} />
          <span>{yaraData.warning}</span>
        </div>
      )}

      <div className="yara-code-container">
        <pre className="yara-code">
          <code>{yaraData.rule || ''}</code>
        </pre>
      </div>

      <div className="yara-note">
        <span className="note-icon">ℹ️</span>
        <span className="note-text">
          Auto-generated from {yaraData.meta?.byte_signatures_extracted} byte signatures.
          Requires {yaraData.meta?.required_matches} matches. Review before production deployment.
        </span>
      </div>
    </div>
  );
};

export default YaraCard;
